import type {
  ConditionalArgument,
  RoutineVariable,
} from '@sb/remote-control/types';

import { formatConditionGroup } from '../conditional/formatCondition';

// Constants for stop early condition values
const NOT_STOPPED_VALUE = '0';
const NOT_STOPPED_LABEL = 'Not stopped';

/**
 * Formats stop early conditions from JSON string into enum options
 *
 * @param stopEarlyConditionsJson - JSON string representing conditional groups
 * @returns Array of enum options with formatted display names
 *
 * @example
 * formatStopEarlyConditionsAsEnumOptions('[[[condition1, condition2]]]')
 * // => [
 * //   { value: '0', displayName: 'Not stopped' },
 * //   { value: '1', displayName: 'Gripper: isOpen == true AND Input 1 > 5' }
 * // ]
 */
export function formatStopEarlyConditionsAsEnumOptions(
  stopEarlyConditionsJson: string,
): Array<RoutineVariable.EnumValue<string>> {
  const options: Array<RoutineVariable.EnumValue<string>> = [
    { value: NOT_STOPPED_VALUE, displayName: NOT_STOPPED_LABEL },
  ];

  // Return early with default if no conditions provided
  if (!stopEarlyConditionsJson) {
    return options;
  }

  try {
    const conditionsList: ConditionalArgument.ConditionalBuilderState[][] =
      JSON.parse(stopEarlyConditionsJson);

    if (!Array.isArray(conditionsList) || conditionsList.length === 0) {
      return options;
    }

    // Each array in conditionsList is an OR group (array of AND groups)
    conditionsList.forEach((andGroup, index) => {
      const label = formatConditionGroup(andGroup);

      if (label) {
        options.push({
          value: String(index + 1), // 1-indexed, as string
          displayName: label,
        });
      }
    });
  } catch (err) {
    // If parsing fails, just return default options
    return options;
  }

  return options;
}
